import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.terminy` tRPC router.
 *
 * Kapacita limitována na 100 (větší akce mimo sál; dřív 12 dle sálu Ulehlova 20A).
 * `datumDo > datumOd` vynucená refinementem.
 */

export const terminStavEnum = z.enum(["PLANOVANY", "NAPLNEN", "UKONCEN", "ZRUSEN"]);
export type TerminStav = z.infer<typeof terminStavEnum>;

/** Vytvoření termínu. Validuje `datumDo > datumOd`. */
export const createTerminSchema = z
  .object({
    productId: z.string().uuid("Neplatné ID produktu"),
    datumOd: z.coerce.date(),
    datumDo: z.coerce.date(),
    misto: z.string().max(500, "Místo musí být 500 znaků nebo méně").optional(),
    kapacitaMax: z.coerce
      .number()
      .int()
      .min(1, "Kapacita musí být alespoň 1")
      .max(100, "Kapacita nesmí překročit 100")
      .optional(),
    lektorId: z.string().max(255, "ID lektora musí být 255 znaků nebo méně").optional(),
    asistentId: z.string().max(255, "ID asistenta musí být 255 znaků nebo méně").optional(),
    poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").optional(),
    fixniNakladyKc: z.coerce
      .number()
      .nonnegative("Fixní náklady nesmí být záporné")
      .max(1_000_000, "Fixní náklady jsou mimo povolený rozsah")
      .optional(),
    /**
     * Paušál za celou akci (interní firemní workshopy — firma platí za den,
     * účastníci se evidují za 0 Kč kvůli prezenčce). Nevyplněno = běžný
     * termín, hodnota se počítá z přihlášek.
     */
    cenaFixniKc: z.coerce
      .number()
      .nonnegative("Fixní cena akce nesmí být záporná")
      .max(10_000_000, "Fixní cena akce je mimo povolený rozsah")
      .optional(),
    /** Zobrazit na veřejném webu. Interní firemní termín se odškrtne. */
    verejny: z.coerce.boolean().optional(),
    /**
     * Automatické e-maily ze sérií (potvrzení, připomínky, follow-up) pro
     * účastníky termínu. Nevyplněno = zapnuto (DB default).
     */
    autoEmaily: z.boolean().optional(),
    /**
     * Položka složení programu, kterou tento termín plní. Server ji naváže
     * ve stejné transakci jako vznik termínu — termín a vazba tak vzniknou
     * buď oba, nebo žádný.
     */
    programSeminarId: optionalUuid("Neplatné ID položky programu"),
  })
  .refine((data) => data.datumDo > data.datumOd, {
    message: "Datum do musí být po datu od",
    path: ["datumDo"],
  });

/** Update termínu — partial + id + volitelně stav. */
export const updateTerminSchema = z
  .object({
    id: z.string().uuid("Neplatné ID termínu"),
    productId: optionalUuid("Neplatné ID produktu"),
    datumOd: z.coerce.date().optional(),
    datumDo: z.coerce.date().optional(),
    misto: z.string().max(500, "Místo musí být 500 znaků nebo méně").optional(),
    kapacitaMax: z.coerce
      .number()
      .int()
      .min(1, "Kapacita musí být alespoň 1")
      .max(100, "Kapacita nesmí překročit 100")
      .optional(),
    mistBlokovano: z.coerce
      .number()
      .int()
      .min(0, "Blokovaná místa nesmí být záporná")
      .max(100, "Blokovaná místa jsou mimo rozsah")
      .optional(),
    lektorId: z.string().max(255, "ID lektora musí být 255 znaků nebo méně").nullable().optional(),
    asistentId: z
      .string()
      .max(255, "ID asistenta musí být 255 znaků nebo méně")
      .nullable()
      .optional(),
    stav: terminStavEnum.optional(),
    poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").nullable().optional(),
    fixniNakladyKc: z.coerce
      .number()
      .nonnegative("Fixní náklady nesmí být záporné")
      .max(1_000_000, "Fixní náklady jsou mimo povolený rozsah")
      .optional(),
    /**
     * Paušál za celou akci. `null` = smazat paušál (termín se vrátí
     * k výpočtu hodnoty z přihlášek) — stejný vzor jako lektorId/poznamka.
     */
    cenaFixniKc: z.coerce
      .number()
      .nonnegative("Fixní cena akce nesmí být záporná")
      .max(10_000_000, "Fixní cena akce je mimo povolený rozsah")
      .nullable()
      .optional(),
    /** Zobrazit na veřejném webu. */
    verejny: z.coerce.boolean().optional(),
    /**
     * Vypínač automatických e-mailů termínu. false = žádné nové běhy sérií
     * a čekající běhy navázané na termín worker zruší; true = připomínky,
     * jejichž čas ještě nenastal, skener obnoví.
     */
    autoEmaily: z.boolean().optional(),
  })
  .refine(
    (data) =>
      data.datumOd === undefined || data.datumDo === undefined || data.datumDo > data.datumOd,
    { message: "Datum do musí být po datu od", path: ["datumDo"] },
  );

/** Whitelist řazení termínů — router mapuje klíče na sloupce/výrazy. */
export const TERMINY_SORT_KEYS = ["datumOd", "produkt", "obsazenost", "stav"] as const;
export type TerminySortKey = (typeof TERMINY_SORT_KEYS)[number];

/** List termínů — paginace + filtry. */
export const listTerminySchema = paginationSchema.merge(sortSchema(TERMINY_SORT_KEYS)).extend({
  productId: optionalUuid(),
  stav: terminStavEnum.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  lektorId: z.string().max(255, "ID lektora musí být 255 znaků nebo méně").optional(),
  /** Fulltext přes název produktu a místo. */
  search: z.string().max(255, "Hledání musí být 255 znaků nebo méně").optional(),
  /** Skrýt už proběhlé termíny (datumDo v minulosti). */
  hidePast: z.boolean().optional(),
  /** Skrýt zrušené/archivované termíny (stav ZRUSEN). */
  hideCancelled: z.boolean().optional(),
});

/** Veřejný list (otevřené termíny s volnou kapacitou). */
export const listPublicTerminySchema = z.object({
  productId: optionalUuid(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

/** ID-only schema (getById / cancel). */
export const terminIdSchema = z.object({
  id: z.string().uuid("Neplatné ID termínu"),
});

/** Změna stavu — id + nový stav. */
export const changeStavSchema = z.object({
  id: z.string().uuid("Neplatné ID termínu"),
  stav: terminStavEnum,
});

/** Zrušit termín — volitelný důvod. */
export const cancelTerminSchema = z.object({
  id: z.string().uuid("Neplatné ID termínu"),
  reason: z.string().max(2000, "Důvod musí být 2000 znaků nebo méně").optional(),
});

export type CreateTerminInput = z.infer<typeof createTerminSchema>;
export type UpdateTerminInput = z.infer<typeof updateTerminSchema>;
export type ListTerminyInput = z.infer<typeof listTerminySchema>;
export type ListPublicTerminyInput = z.infer<typeof listPublicTerminySchema>;
export type TerminIdInput = z.infer<typeof terminIdSchema>;
export type ChangeStavInput = z.infer<typeof changeStavSchema>;
export type CancelTerminInput = z.infer<typeof cancelTerminSchema>;
